/**
 * landingV3.seo.js — el `<title>`, la descripción y la imagen social de cada
 * página pública, en los cinco idiomas.
 *
 * ── POR QUÉ NO ESTÁ DENTRO DEL COPY DE CADA PÁGINA ──────────────────────────
 *
 * Porque no es el mismo oficio. El copy de la página lo lee una persona que ya
 * está dentro; esto lo lee alguien que está mirando una lista de diez
 * resultados en Google y decide en dos segundos a cuál entra. Se escribe con la
 * palabra clave delante, con un límite de caracteres duro y sin repetir el
 * titular de la página.
 *
 * Y se escribe COMPARANDO: el fallo típico es que diez páginas acaben con el
 * mismo título («MyTapstar — SEO local»), y eso solo se ve teniéndolos juntos.
 * Por eso la tabla va por página y dentro por idioma, y no al revés.
 *
 * ── LOS LÍMITES, Y POR QUÉ SON LOS QUE SON ──────────────────────────────────
 *
 * Google no corta por caracteres sino por ANCHO en píxeles, pero como regla de
 * trabajo: **título ≤ 60** y **descripción ≤ 160**. Pasarse no penaliza el
 * posicionamiento; lo que hace es que Google te corte la frase a media palabra
 * o se invente otra. `landingV3Seo.test.js` lo comprueba, junto con que no haya
 * dos páginas con el mismo título en un idioma.
 *
 * La marca va al FINAL y escrita en cada cadena, no pegada por el hook: así lo
 * que hay en el fichero es exactamente lo que sale, el límite se cuenta sobre
 * el texto real y una página puede prescindir de ella si le hace falta el sitio.
 */

/**
 * Las imágenes de Open Graph — las que salen al compartir un enlace en
 * WhatsApp, LinkedIn o Slack.
 *
 * Son PNG de 1200×630 generadas a partir de las capturas que ya teníamos
 * (`scripts/generar-og.py`). Las capturas de origen son WebP y ninguna tiene
 * esa proporción, y las dos cosas importan:
 *
 *  - **El formato**: LinkedIn y WhatsApp no renderizan WebP de forma fiable en
 *    `og:image`. Un enlace compartido sin imagen pierde la mitad del clic.
 *  - **La proporción**: 1,91:1 es lo que esperan. Con otra, cada plataforma
 *    recorta por su cuenta y suele cortar justo por el medio de la captura.
 *
 * `defecto` la usan la home, los cuatro sectores y el widget. De esas cinco no
 * tenemos captura propia: inventarse una sería fabricar una pantalla de
 * producto que no existe, así que comparten la del Mapa SEO, que es la que
 * mejor resume qué hace esto.
 */
const OG = {
  defecto: '/landing-images/v3/og/og-default.png',
  visibilidadSeo: '/landing-images/v3/og/og-visibilidad-seo.png',
  automatizaciones: '/landing-images/v3/og/og-automatizaciones.png',
  publicacionesIa: '/landing-images/v3/og/og-publicaciones-ia.png',
  multiubicaciones: '/landing-images/v3/og/og-multiubicaciones.png',
};

/** Alto y ancho de todas las de arriba. Van en `og:image:width/height` para que
 *  la plataforma reserve el hueco antes de descargarla. */
export const OG_ANCHO = 1200;
export const OG_ALTO = 630;

export const SEO = {
  home: {
    imagen: OG.defecto,
    es: {
      titulo: 'Sube en Google Maps y gestiona tus reseñas | MyTapstar',
      descripcion: 'Escanea tu posición en Google Maps punto por punto, responde reseñas con IA y publica en tu ficha, todo desde un panel. Prueba 7 días gratis.',
    },
    en: {
      titulo: 'Rank higher on Google Maps and manage reviews | MyTapstar',
      descripcion: 'Scan your position on Google Maps point by point, answer reviews with AI and post to your listing, all from one dashboard. Try 7 days free.',
    },
    pt: {
      titulo: 'Suba no Google Maps e gira as suas avaliações | MyTapstar',
      descripcion: 'Analise a sua posição no Google Maps ponto a ponto, responda às avaliações com IA e publique na sua ficha, tudo num painel. Teste 7 dias grátis.',
    },
    de: {
      titulo: 'Bei Google Maps steigen, Bewertungen verwalten | MyTapstar',
      descripcion: 'Scanne deine Position bei Google Maps Punkt für Punkt, beantworte Bewertungen mit KI und poste in deinem Profil — alles in einem Dashboard.',
    },
    fr: {
      titulo: 'Montez sur Google Maps et gérez vos avis | MyTapstar',
      descripcion: 'Analysez votre position sur Google Maps point par point, répondez aux avis avec l’IA et publiez sur votre fiche, depuis un seul tableau de bord.',
    },
  },

  multiubicaciones: {
    imagen: OG.multiubicaciones,
    es: {
      titulo: 'Gestiona varios locales en Google Maps | MyTapstar',
      descripcion: 'Un panel para las fichas de Google de todos tus locales: compara su rendimiento, responde las reseñas en una bandeja única y cambia horarios en bloque.',
    },
    en: {
      titulo: 'Manage several locations on Google Maps | MyTapstar',
      descripcion: 'One dashboard for every location’s Google listing: compare performance, answer reviews in a single inbox and change opening hours in bulk.',
    },
    pt: {
      titulo: 'Gestão de vários locais no Google Maps | MyTapstar',
      descripcion: 'Um painel para as fichas do Google de todos os seus locais: compare o desempenho, responda às avaliações numa caixa única e mude horários em bloco.',
    },
    de: {
      titulo: 'Mehrere Standorte bei Google Maps verwalten | MyTapstar',
      descripcion: 'Ein Dashboard für die Google-Profile all deiner Standorte: Leistung vergleichen, Bewertungen in einem Posteingang beantworten, Zeiten im Block ändern.',
    },
    fr: {
      titulo: 'Gérez plusieurs établissements sur Google Maps | MyTapstar',
      descripcion: 'Un tableau de bord pour les fiches Google de tous vos établissements : comparez les performances, répondez aux avis et modifiez les horaires en lot.',
    },
  },

  visibilidadSeo: {
    imagen: OG.visibilidadSeo,
    es: {
      titulo: 'Mapa SEO: tu posición real en Google Maps | MyTapstar',
      descripcion: 'Descubre en qué puesto sales en Google Maps desde cada punto de tu zona, compárate con tus competidores y sabe qué te frena, con una puntuación sobre 100.',
    },
    en: {
      titulo: 'SEO map: your real position on Google Maps | MyTapstar',
      descripcion: 'Find out what position you come up in on Google Maps from every point in your area, compare with competitors and see what holds you back, scored out of 100.',
    },
    pt: {
      titulo: 'Mapa de SEO: a sua posição no Google Maps | MyTapstar',
      descripcion: 'Descubra em que posição aparece no Google Maps a partir de cada ponto da sua zona, compare-se com os concorrentes e veja o que o trava, numa pontuação até 100.',
    },
    de: {
      titulo: 'SEO-Karte: deine Position bei Google Maps | MyTapstar',
      descripcion: 'Finde heraus, auf welcher Position du bei Google Maps von jedem Punkt deiner Gegend aus erscheinst, vergleich dich mit Wettbewerbern und sieh, was dich bremst.',
    },
    fr: {
      titulo: 'Carte SEO : votre vraie position sur Google Maps',
      descripcion: 'Découvrez à quelle position vous apparaissez sur Google Maps depuis chaque point de votre zone, comparez-vous aux concurrents et voyez ce qui vous freine.',
    },
  },

  automatizaciones: {
    imagen: OG.automatizaciones,
    es: {
      titulo: 'Responde reseñas de Google con IA | MyTapstar',
      descripcion: 'Configura las reglas una vez y cada reseña se responde sola con el tono de tu marca. Te avisamos al instante si llega una mala o mencionan una palabra clave.',
    },
    en: {
      titulo: 'Answer Google reviews automatically with AI | MyTapstar',
      descripcion: 'Set the rules once and every review is answered on its own in your brand’s tone. And you hear straight away if a bad one lands or a keyword comes up.',
    },
    pt: {
      titulo: 'Responda a avaliações do Google com IA | MyTapstar',
      descripcion: 'Configure as regras uma vez e cada avaliação responde-se sozinha com o tom da sua marca. E avisamos de imediato se chegar uma má ou mencionarem uma palavra.',
    },
    de: {
      titulo: 'Google-Bewertungen mit KI beantworten | MyTapstar',
      descripcion: 'Richte die Regeln einmal ein und jede Bewertung wird von allein in deinem Markenton beantwortet. Und du erfährst sofort, wenn eine schlechte eingeht.',
    },
    fr: {
      titulo: 'Répondez aux avis Google avec l’IA | MyTapstar',
      descripcion: 'Configurez les règles une fois et chaque avis reçoit une réponse dans le ton de votre marque. Et vous êtes prévenu tout de suite si un mauvais arrive.',
    },
  },

  publicacionesIa: {
    imagen: OG.publicacionesIa,
    es: {
      titulo: 'Publicaciones para tu ficha de Google con IA | MyTapstar',
      descripcion: 'Escribe la idea y la IA redacta la publicación. Prográmala hasta con 180 días de antelación y sale adaptada en cada uno de tus locales.',
    },
    en: {
      titulo: 'AI posts for your Google Business listing | MyTapstar',
      descripcion: 'Write the idea and AI drafts the post. Schedule it up to 180 days ahead and it goes out adapted at every one of your locations.',
    },
    pt: {
      titulo: 'Publicações para a sua ficha do Google com IA | MyTapstar',
      descripcion: 'Escreva a ideia e a IA redige a publicação. Agende-a até com 180 dias de antecedência e sai adaptada em cada um dos seus locais.',
    },
    de: {
      titulo: 'KI-Beiträge für dein Google-Unternehmensprofil | MyTapstar',
      descripcion: 'Schreib die Idee und die KI verfasst den Beitrag. Plane ihn bis zu 180 Tage im Voraus — er geht an jedem deiner Standorte angepasst raus.',
    },
    fr: {
      titulo: 'Publications IA pour votre fiche Google | MyTapstar',
      descripcion: 'Écrivez l’idée et l’IA rédige la publication. Programmez-la jusqu’à 180 jours à l’avance : elle sort adaptée dans chacun de vos établissements.',
    },
  },

  widgetResenas: {
    imagen: OG.defecto,
    es: {
      titulo: 'Widget de reseñas de Google para tu web | MyTapstar',
      descripcion: 'Muestra tus reseñas de Google en tu propia web con dos líneas de código. Resumen o carrusel, con los colores de tu marca y actualizado solo.',
    },
    en: {
      titulo: 'Google reviews widget for your website | MyTapstar',
      descripcion: 'Show your Google reviews on your own website with two lines of code. Summary or carousel, in your brand colours and updated on its own.',
    },
    pt: {
      titulo: 'Widget de avaliações do Google para o seu site | MyTapstar',
      descripcion: 'Mostre as suas avaliações do Google no seu site com duas linhas de código. Resumo ou carrossel, com as cores da sua marca e atualizado sozinho.',
    },
    de: {
      titulo: 'Google-Bewertungs-Widget für deine Website | MyTapstar',
      descripcion: 'Zeig deine Google-Bewertungen auf deiner eigenen Website — mit zwei Zeilen Code. Zusammenfassung oder Karussell, in deinen Markenfarben.',
    },
    fr: {
      titulo: 'Widget d’avis Google pour votre site web | MyTapstar',
      descripcion: 'Affichez vos avis Google sur votre propre site avec deux lignes de code. Résumé ou carrousel, aux couleurs de votre marque et mis à jour tout seul.',
    },
  },

  restauracion: {
    imagen: OG.defecto,
    es: {
      titulo: 'SEO local para restaurantes en Google Maps | MyTapstar',
      descripcion: 'Que cada uno de tus restaurantes salga el primero cuando buscan «sushi cerca de mí». Reseñas respondidas, ofertas publicadas y tu posición vigilada.',
    },
    en: {
      titulo: 'Local SEO for restaurants on Google Maps | MyTapstar',
      descripcion: 'Make every one of your restaurants come up first when people search “sushi near me”. Reviews answered, offers posted and your position watched.',
    },
    pt: {
      titulo: 'SEO local para restaurantes no Google Maps | MyTapstar',
      descripcion: 'Que cada um dos seus restaurantes apareça em primeiro quando procuram «sushi perto de mim». Avaliações respondidas e a sua posição vigiada.',
    },
    de: {
      titulo: 'Lokales SEO für Restaurants bei Google Maps | MyTapstar',
      descripcion: 'Damit jedes deiner Restaurants zuerst erscheint, wenn jemand „Sushi in meiner Nähe“ sucht. Bewertungen beantwortet und deine Position im Blick.',
    },
    fr: {
      titulo: 'SEO local pour restaurants sur Google Maps | MyTapstar',
      descripcion: 'Que chacun de vos restaurants apparaisse en premier quand on cherche « sushi près de moi ». Avis traités, offres publiées et position surveillée.',
    },
  },

  clinicas: {
    imagen: OG.defecto,
    es: {
      titulo: 'SEO local para clínicas y centros de salud | MyTapstar',
      descripcion: 'Aparece el primero cuando buscan «dentista cerca de mí». Reseñas respondidas con prudencia profesional y tu posición en Google Maps, vigilada.',
    },
    en: {
      titulo: 'Local SEO for clinics and health centres | MyTapstar',
      descripcion: 'Come up first when people search “dentist near me”. Reviews answered with professional care and your position on Google Maps kept under watch.',
    },
    pt: {
      titulo: 'SEO local para clínicas e centros de saúde | MyTapstar',
      descripcion: 'Apareça em primeiro quando procuram «dentista perto de mim». Avaliações respondidas com prudência profissional e a sua posição vigiada.',
    },
    de: {
      titulo: 'Lokales SEO für Praxen und Kliniken | MyTapstar',
      descripcion: 'Erscheine zuerst, wenn jemand „Zahnarzt in meiner Nähe“ sucht. Bewertungen mit fachlicher Umsicht beantwortet und deine Position im Blick.',
    },
    fr: {
      titulo: 'SEO local pour cliniques et centres de santé | MyTapstar',
      descripcion: 'Apparaissez en premier quand on cherche « dentiste près de moi ». Des avis traités avec prudence et votre position sur Google Maps surveillée.',
    },
  },

  automotriz: {
    imagen: OG.defecto,
    es: {
      titulo: 'SEO local para talleres y concesionarios | MyTapstar',
      descripcion: 'Cuando el coche falla se busca «taller cerca de mí». Sal el primero: reseñas respondidas, campañas de temporada publicadas y tu posición vigilada.',
    },
    en: {
      titulo: 'Local SEO for garages and dealerships | MyTapstar',
      descripcion: 'When the car plays up, people search “garage near me”. Come up first: reviews answered, seasonal campaigns posted and your position watched.',
    },
    pt: {
      titulo: 'SEO local para oficinas e concessionários | MyTapstar',
      descripcion: 'Quando o carro avaria procura-se «oficina perto de mim». Apareça em primeiro: avaliações respondidas, campanhas publicadas e posição vigiada.',
    },
    de: {
      titulo: 'Lokales SEO für Werkstätten und Autohäuser | MyTapstar',
      descripcion: 'Wenn das Auto streikt, sucht man „Werkstatt in meiner Nähe“. Erscheine zuerst: Bewertungen beantwortet und deine Position im Blick.',
    },
    fr: {
      titulo: 'SEO local pour garages et concessions | MyTapstar',
      descripcion: 'Quand la voiture lâche, on cherche « garage près de moi ». Apparaissez en premier : avis traités, campagnes publiées et position surveillée.',
    },
  },

  hoteles: {
    imagen: OG.defecto,
    es: {
      titulo: 'SEO local para hoteles en Google Maps | MyTapstar',
      descripcion: 'Responde las reseñas en el idioma de cada huésped, publica tus ofertas y vigila tu posición en Google Maps en todos tus establecimientos.',
    },
    en: {
      titulo: 'Local SEO for hotels on Google Maps | MyTapstar',
      descripcion: 'Answer reviews in each guest’s language, post your offers and watch your position on Google Maps across all your properties.',
    },
    pt: {
      titulo: 'SEO local para hotéis no Google Maps | MyTapstar',
      descripcion: 'Responda às avaliações no idioma de cada hóspede, publique as suas ofertas e vigie a sua posição no Google Maps em todos os estabelecimentos.',
    },
    de: {
      titulo: 'Lokales SEO für Hotels bei Google Maps | MyTapstar',
      descripcion: 'Beantworte Bewertungen in der Sprache jedes Gasts, veröffentliche deine Angebote und behalte deine Position bei Google Maps in allen Häusern im Blick.',
    },
    fr: {
      titulo: 'SEO local pour hôtels sur Google Maps | MyTapstar',
      descripcion: 'Répondez aux avis dans la langue de chaque client, publiez vos offres et surveillez votre position sur Google Maps dans tous vos établissements.',
    },
  },

  /*
   * Precios.
   *
   * La descripción dice «gratis» y «por local» porque son las dos cosas que
   * decide el clic en una lista de resultados de intención comercial: si hay
   * plan gratuito y cómo se cobra. El importe NO se pone: sale de
   * `/billing/active-pricing` y una cifra escrita aquí se quedaría vieja en
   * silencio la próxima vez que cambie la tarifa — que ha cambiado cuatro veces.
   */
  precios: {
    imagen: OG.defecto,
    es: {
      titulo: 'Precios de MyTapstar: plan gratis y Business | MyTapstar',
      descripcion: 'Conecta tu ficha de Google gratis, para siempre. Business se paga por local activo, sin permanencia y con prueba gratuita.',
    },
    en: {
      titulo: 'MyTapstar pricing: free plan and Business | MyTapstar',
      descripcion: 'Connect your Google Business Profile free, forever. Business is billed per active location, with no minimum term and a free trial to start.',
    },
    pt: {
      titulo: 'Preços da MyTapstar: plano grátis e Business | MyTapstar',
      descripcion: 'Ligue a sua ficha do Google grátis, para sempre. O Business paga-se por estabelecimento ativo, sem fidelização e com período experimental.',
    },
    de: {
      titulo: 'MyTapstar Preise: Gratis-Plan und Business | MyTapstar',
      descripcion: 'Verbinde dein Google-Unternehmensprofil kostenlos, für immer. Business wird pro aktivem Standort abgerechnet, ohne Mindestlaufzeit und mit Testphase.',
    },
    fr: {
      titulo: 'Tarifs MyTapstar : plan gratuit et Business | MyTapstar',
      descripcion: 'Connectez votre fiche Google gratuitement, pour toujours. Business se facture par établissement actif, sans engagement et avec essai gratuit.',
    },
  },

  /*
   * Sobre nosotros.
   *
   * Es la página que sale cuando alguien busca la marca con «quiénes son»,
   * «empresa» o «Valencia», así que el título lleva las tres señales que
   * resuelven esa duda: quiénes, dónde y qué hacemos. Sin cifras: las de la
   * home están sin confirmar y aquí se quedarían escritas en el snippet.
   */
  sobreNosotros: {
    imagen: OG.defecto,
    es: {
      titulo: 'Sobre nosotros: el equipo de MyTapstar en Valencia',
      descripcion: 'Somos un equipo de Valencia que crea dispositivos y software para que los negocios locales consigan más reseñas y destaquen en Google Maps.',
    },
    en: {
      titulo: 'About us: the MyTapstar team in Valencia, Spain',
      descripcion: 'We are a team in Valencia building devices and software that help local businesses earn more reviews and stand out on Google Maps.',
    },
    pt: {
      titulo: 'Sobre nós: a equipa da MyTapstar em Valência',
      descripcion: 'Somos uma equipa de Valência que cria dispositivos e software para que os negócios locais tenham mais avaliações e se destaquem no Google Maps.',
    },
    de: {
      titulo: 'Über uns: das MyTapstar-Team in Valencia',
      descripcion: 'Wir sind ein Team aus Valencia und entwickeln Geräte und Software, mit denen lokale Unternehmen mehr Bewertungen sammeln und in Google Maps auffallen.',
    },
    fr: {
      titulo: 'À propos : l’équipe MyTapstar à Valence',
      descripcion: 'Nous sommes une équipe basée à Valence qui crée des appareils et un logiciel pour aider les commerces locaux à obtenir plus d’avis sur Google Maps.',
    },
  },
};

/**
 * El `<title>`, la descripción y la imagen de una página en un idioma.
 *
 * Cae a español si falta el idioma, igual que `pickLandingV3Content`, y a
 * `null` si la página no está en la tabla — que es el caso de las legales y de
 * `/auditor`, que no llevan SEO propio a propósito.
 */
export function seoDe(pagina, idioma) {
  const ficha = SEO[pagina];
  if (!ficha) return null;
  const textos = ficha[idioma] || ficha.es;
  return { ...textos, imagen: ficha.imagen };
}
